/**
 * Vaperite Stores.
 *
 * Three views over the Vaperite estate:
 *  - Map (default) — the branches trading with Deeliver, plotted so the spread
 *    reads at a glance.
 *  - Active Stores — those same branches with the detail Vaperite supply.
 *  - All Stores — every branch, trading or not.
 *
 * Data comes from the store master (see data/vaperiteStores.ts) rather than the
 * `stores` table, which has no columns for address, trading hours, email or
 * storefront links.
 */

import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Chip } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Title } from 'react-admin';
import { useAdminTheme } from '../admin-theme';
import { VaperiteStoreMap } from '../components/VaperiteStores/VaperiteStoreMap';
import { ActiveStoresTable } from '../components/VaperiteStores/ActiveStoresTable';
import { AllStoresTable } from '../components/VaperiteStores/AllStoresTable';
import { VAPERITE_STORES, ACTIVE_VAPERITE_STORES } from '../data/vaperiteStores';

type View = 'map' | 'active' | 'all';

export default function VaperiteStores(): React.ReactElement {
  const { colors, glassHeaderStyle } = useAdminTheme();
  const [view, setView] = useState<View>('map');

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh', overflowX: 'clip' }}>
      <Title title="Vaperite Stores" />

      <Box sx={{ ...glassHeaderStyle, px: 3, pt: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 800 }}>
            Vaperite Stores
          </Typography>
          <Chip
            size="small"
            label={`${ACTIVE_VAPERITE_STORES.length} active · ${VAPERITE_STORES.length} total`}
            sx={{ bgcolor: `${colors.info}1a`, color: colors.info, fontSize: '0.7rem', height: 22 }}
          />
        </Box>

        <Tabs
          value={view}
          onChange={(_, v: View) => setView(v)}
          sx={{
            mt: 1,
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: colors.textMuted,
            },
            '& .Mui-selected': { color: `${colors.textPrimary} !important` },
            '& .MuiTabs-indicator': { backgroundColor: colors.info },
          }}
        >
          <Tab value="map" label="Map" icon={<MapIcon fontSize="small" />} iconPosition="start" />
          <Tab value="active" label="Active Stores" icon={<StorefrontIcon fontSize="small" />} iconPosition="start" />
          <Tab value="all" label="All Stores" icon={<ListAltIcon fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Remounting the map on tab change is deliberate: Leaflet measures its
          container on mount, and a map built while hidden renders grey tiles. */}
      {view === 'map' && <VaperiteStoreMap />}
      {view === 'active' && <ActiveStoresTable />}
      {view === 'all' && <AllStoresTable />}
    </Box>
  );
}
